import {useCallback, useEffect, useMemo, useState} from 'react';
import {Select} from '@gravity-ui/uikit';
import {useDebounce} from '../../../../shared/lib/hooks/useDebounce';
import {useFunds} from '../../lib';
import {FundModel, FundWithMetaModel} from '../../model';

/** Размер страницы списка Фондов: остальные подгружаются по мере прокрутки. */
const FUNDS_PAGE_SIZE = 50;

/** Задержка перед отправкой поискового запроса на сервер, мс. */
const SEARCH_DEBOUNCE_DELAY = 300;

interface SelectFundOption {
  value: string;
  content: string;
}

interface SelectFundProps {
  value: string;
  onUpdate: (value: string) => void;
  error?: string | false;
  placeholder?: string;
  size?: 's' | 'm' | 'l' | 'xl';
}

export const SelectFund = ({
  value,
  onUpdate,
  error,
  placeholder = 'Выберите фонд',
  size = 'm',
}: SelectFundProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_DELAY).trim();

  const {getInfiniteFunds, updateFilter} = useFunds({pageSize: FUNDS_PAGE_SIZE});
  const {data, fetchNextPage, hasNextPage, isFetching} = getInfiniteFunds;

  // Поиск выполняется на сервере: локально отфильтровать можно было бы только загруженные страницы
  useEffect(() => {
    updateFilter({query: debouncedQuery});
  }, [debouncedQuery, updateFilter]);

  // Хранит отображаемое имя выбранного Фонда, чтобы Select показывал его
  // даже когда поисковый запрос исключил этот Фонд из текущих результатов
  const [selectedOption, setSelectedOption] = useState<SelectFundOption | null>(null);

  const fundOptions = useMemo<SelectFundOption[]>(() => {
    return (
      data?.pages.flatMap((page: FundWithMetaModel) =>
        page.items.map((fund: FundModel) => ({
          value: fund.id,
          content: fund.name,
        })),
      ) ?? []
    );
  }, [data]);

  const optionsWithSelected = useMemo<SelectFundOption[]>(() => {
    // Подставляем сохранённую опцию только если она соответствует текущему value
    // и отсутствует в текущих результатах поиска. Это не даёт устаревшему Фонду
    // протечь в форму, value которой очищено или указывает на другой Фонд.
    const shouldPrependSelected =
      selectedOption &&
      value === selectedOption.value &&
      !fundOptions.some((o) => o.value === selectedOption.value);
    return shouldPrependSelected ? [selectedOption, ...fundOptions] : fundOptions;
  }, [fundOptions, selectedOption, value]);

  const handleUpdate = useCallback(
    (newValue: string[]) => {
      const id = newValue[0] ?? '';
      const opt = fundOptions.find((o) => o.value === id);
      if (opt) setSelectedOption(opt);
      onUpdate(id);
    },
    [fundOptions, onUpdate],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) setSearchQuery('');
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  return (
    <Select
      size={size}
      options={optionsWithSelected}
      onUpdate={handleUpdate}
      value={value ? [value] : []}
      // Select подмешивает в конец списка индикатор загрузки только при loading, и лишь его
      // появление при прокрутке вызывает onLoadMore — поэтому loading значит «есть что грузить»
      loading={isFetching || hasNextPage}
      onLoadMore={handleLoadMore}
      placeholder={placeholder}
      errorPlacement="inside"
      errorMessage={error || undefined}
      validationState={error ? 'invalid' : undefined}
      filterable
      filterPlaceholder="Найти фонд"
      onFilterChange={setSearchQuery}
      filterOption={() => true}
      renderEmptyOptions={() => <span>Фонд не найден</span>}
      onOpenChange={handleOpenChange}
      width="max"
    />
  );
};
